import { supabase } from './supabase';

/**
 * City-hero (`cities.hero_image_url`) crop/upload helpers — the city-page analogue of the
 * venue-photo crop flow in `cropImage.ts`. Reuses `cropImageToJpegBlob` (bucket-agnostic) for
 * the canvas crop; only the storage bucket differs (`city-heroes` vs `venue-photos`).
 */
const CITY_HEROES_BUCKET = 'city-heroes';

/** Extract the storage object path from a `city-heroes` public URL, if applicable. */
export function cityHeroStoragePath(publicUrl: string): string | null {
  const marker = `/${CITY_HEROES_BUCKET}/`;
  const idx = publicUrl.indexOf(marker);
  if (idx < 0) return null;
  const path = publicUrl.slice(idx + marker.length).split('?')[0];
  return path || null;
}

/** Upload a cropped JPEG blob to `city-heroes` and return its public URL. */
export async function uploadCityHeroBlob(
  path: string,
  blob: Blob,
): Promise<{ publicUrl: string; storagePath: string }> {
  const { error } = await supabase.storage.from(CITY_HEROES_BUCKET).upload(path, blob, {
    contentType: 'image/jpeg',
    upsert: true,
  });
  if (error) throw error;
  const { data } = supabase.storage.from(CITY_HEROES_BUCKET).getPublicUrl(path);
  if (!data.publicUrl) throw new Error('Could not resolve public URL after upload');
  return { publicUrl: data.publicUrl, storagePath: path };
}

/**
 * Prefer a signed URL for canvas cropping. The `city-heroes` bucket is public, but signed URLs
 * are still the CORS-safe path for `<canvas>.drawImage` (avoids tainting the canvas), matching
 * how the venue flow resolves its crop source.
 */
export async function resolveCityHeroCropUrl(publicUrl: string): Promise<string> {
  const path = cityHeroStoragePath(publicUrl);
  if (path) {
    const { data, error } = await supabase.storage
      .from(CITY_HEROES_BUCKET)
      .createSignedUrl(path, 3600);
    if (!error && data?.signedUrl) return data.signedUrl;
  }
  return publicUrl;
}
